import apiClient, { ApiRequestError, getApiErrorMessage } from './apiClient';
import { DEFAULT_LLM_PROVIDER_KEY } from '../constants/llmProviders';

const GENERATE_TIMEOUT_MS = 180000;

const unwrapApiData = (data) => data?.data || data?.Data || data;
const unwrapApiList = (data) => {
  const unwrapped = unwrapApiData(data);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (Array.isArray(unwrapped?.items)) return unwrapped.items;
  if (Array.isArray(unwrapped?.Items)) return unwrapped.Items;
  return [];
};

const toApiError = (error, fallbackMessage) => {
  const status = error?.response?.status;
  const wrapped = new ApiRequestError(getApiErrorMessage(error, fallbackMessage), status);
  if (error?.code === 'ECONNABORTED') {
    wrapped.code = 'TIMEOUT';
  } else if (!error?.response && error?.message === 'Network Error') {
    wrapped.code = 'NETWORK';
  }
  return wrapped;
};

export const normalizeImageSession = (item) => ({
  id: item?.Id || item?.id,
  title: item?.Title || item?.title || '',
  model: item?.Model || item?.model || 'dall-e-3',
  provider: item?.Provider || item?.provider || DEFAULT_LLM_PROVIDER_KEY,
  createdAt: item?.CreatedAt || item?.createdAt || new Date().toISOString(),
  updatedAt: item?.UpdatedAt || item?.updatedAt || new Date().toISOString(),
  isPinned: Boolean(item?.IsPinned ?? item?.isPinned),
  isArchived: Boolean(item?.IsArchived ?? item?.isArchived),
  folderId: item?.FolderId || item?.folderId || null
});

export const normalizeImageMessage = (message, index = 0) => {
  const text = message?.Message || message?.message || '';
  return {
    id: message?.Id || message?.id || `img-msg-${index}`,
    imageSessionId: message?.ImageSessionId || message?.imageSessionId,
    role: `${message?.Role || message?.role || 'assistant'}`.toLowerCase(),
    provider: message?.Provider || message?.provider || null,
    model: message?.Model || message?.model || null,
    message: text,
    content: text,
    imageUrls: message?.ImageUrls || message?.imageUrls || [],
    templateId: message?.TemplateId || message?.templateId || null,
    createdAt: message?.CreatedAt || message?.createdAt || new Date().toISOString(),
    updatedAt: message?.UpdatedAt || message?.updatedAt || new Date().toISOString()
  };
};

export const normalizeLibraryItem = (item, index = 0) => ({
  messageId: item?.MessageId || item?.messageId || `lib-${index}`,
  imageSessionId: item?.ImageSessionId || item?.imageSessionId,
  url: item?.Url || item?.url || '',
  prompt: item?.Prompt || item?.prompt || '',
  model: item?.Model || item?.model || '',
  provider: item?.Provider || item?.provider || '',
  createdAt: item?.CreatedAt || item?.createdAt || new Date().toISOString()
});

export const libraryItemKey = (item) => `${item?.messageId || ''}::${item?.url || ''}`;

export const itemsFromAssistantMessage = (message, prompt = '') =>
  (message?.imageUrls || []).filter(Boolean).map((url) => ({
    messageId: message.id,
    imageSessionId: message.imageSessionId,
    url,
    prompt,
    model: message.model || '',
    provider: message.provider || '',
    createdAt: message.createdAt
  }));

export const normalizePromptCategory = (category) => ({
  id: category?.Id || category?.id,
  slug: category?.Slug || category?.slug || '',
  titleFa: category?.TitleFa || category?.titleFa || '',
  titleEn: category?.TitleEn || category?.titleEn || '',
  sortOrder: category?.SortOrder ?? category?.sortOrder ?? 0,
  templates: unwrapApiList(category?.Templates || category?.templates).map((template) => ({
    id: template?.Id || template?.id,
    categoryId: template?.CategoryId || template?.categoryId,
    titleFa: template?.TitleFa || template?.titleFa || '',
    titleEn: template?.TitleEn || template?.titleEn || '',
    prompt: template?.Prompt || template?.prompt || '',
    thumbnailUrl: template?.ThumbnailUrl || template?.thumbnailUrl || '',
    sortOrder: template?.SortOrder ?? template?.sortOrder ?? 0
  }))
});

export const fetchImageSessionsApi = async () => {
  try {
    const { data } = await apiClient.get('/image-sessions');
    return unwrapApiList(data).map(normalizeImageSession).filter((item) => item.id);
  } catch (error) {
    throw toApiError(error, 'Failed to load image sessions.');
  }
};

export const createImageSessionApi = async ({ title = '', model = 'dall-e-3', provider = DEFAULT_LLM_PROVIDER_KEY } = {}) => {
  try {
    const { data } = await apiClient.post('/image-sessions', {
      Title: title || undefined,
      Model: model,
      Provider: provider
    });
    return normalizeImageSession(unwrapApiData(data));
  } catch (error) {
    throw toApiError(error, 'Failed to create image session.');
  }
};

export const deleteImageSessionApi = async (id) => {
  try {
    await apiClient.delete(`/image-sessions/${encodeURIComponent(id)}`);
    return id;
  } catch (error) {
    throw toApiError(error, 'Failed to delete image session.');
  }
};

export const fetchImageMessagesApi = async (sessionId) => {
  try {
    const { data } = await apiClient.get(`/image-sessions/${encodeURIComponent(sessionId)}/messages`);
    return unwrapApiList(data).map(normalizeImageMessage);
  } catch (error) {
    throw toApiError(error, 'Failed to load image messages.');
  }
};

export const fetchImageLibraryApi = async ({ page = 1, pageSize = 24 } = {}) => {
  try {
    const { data } = await apiClient.get('/image-sessions/library', { params: { page, pageSize } });
    const payload = unwrapApiData(data) || data;
    const items = unwrapApiList(payload).map(normalizeLibraryItem).filter((item) => item.url);
    return {
      items,
      page: payload?.Page ?? payload?.page ?? page,
      pageSize: payload?.PageSize ?? payload?.pageSize ?? pageSize,
      totalCount: payload?.TotalCount ?? payload?.totalCount ?? items.length
    };
  } catch (error) {
    throw toApiError(error, 'Failed to load images.');
  }
};

export const generateImageApi = async ({
  imageSessionId,
  prompt,
  provider = DEFAULT_LLM_PROVIDER_KEY,
  model = 'dall-e-3',
  templateId = null,
  size = '1024x1024',
  imageUrls = []
}) => {
  try {
    const { data } = await apiClient.post(
      '/image-sessions/generate',
      {
        ImageSessionId: imageSessionId,
        Prompt: prompt,
        Provider: provider,
        Model: model,
        TemplateId: templateId || undefined,
        Size: size,
        ImageUrls: Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : []
      },
      { timeout: GENERATE_TIMEOUT_MS }
    );
    return normalizeImageMessage(unwrapApiData(data));
  } catch (error) {
    throw toApiError(error, 'Image generation failed.');
  }
};

export const deleteGeneratedImageApi = async ({ sessionId, messageId, url }) => {
  try {
    await apiClient.delete(
      `/image-sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}`,
      { params: url ? { url } : undefined }
    );
    return { sessionId, messageId, url };
  } catch (error) {
    throw toApiError(error, 'Failed to delete image.');
  }
};

const parseFilename = (disposition) => {
  if (!disposition || typeof disposition !== 'string') return '';
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      return utfMatch[1];
    }
  }
  const asciiMatch = disposition.match(/filename="?([^"]+)"?/i);
  return asciiMatch?.[1] || '';
};

export const downloadGeneratedImageApi = async ({ sessionId, messageId, url }) => {
  try {
    const response = await apiClient.get(
      `/image-sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/download`,
      {
        params: url ? { url } : undefined,
        responseType: 'blob'
      }
    );
    const filename = parseFilename(response.headers?.['content-disposition']) || 'risman-image.png';
    return { blob: response.data, filename };
  } catch (error) {
    throw toApiError(error, 'Failed to download image.');
  }
};

export const fetchImagePromptCatalogApi = async () => {
  try {
    const { data } = await apiClient.get('/image-prompts');
    return unwrapApiList(data).map(normalizePromptCategory);
  } catch (error) {
    throw toApiError(error, 'Failed to load prompt catalog.');
  }
};
